import React, { useEffect, useMemo, useRef, useState } from 'react';
import HomeIcon from './HomeIcon';
import MarketingHeader from './MarketingHeader';
import type { AboutLandingVals } from './useAboutLanding';
import { getAboutCopy } from './aboutCopy';
import './HomeLanding.css';

type Props = {
  v: AboutLandingVals;
  onSetCz: () => void;
  onSetEn: () => void;
  lang: 'cs' | 'en';
};

const AboutLanding: React.FC<Props> = ({ v, onSetCz, onSetEn, lang }) => {
  const c = getAboutCopy(lang);
  const quoteCount = v.quotes.length;
  const loopQuotes = useMemo(
    () => (quoteCount ? [...v.quotes, ...v.quotes, ...v.quotes] : []),
    [v.quotes, quoteCount],
  );
  const [trackIndex, setTrackIndex] = useState(quoteCount); // start in middle copy
  const [animateTrack, setAnimateTrack] = useState(true);
  const [carouselPaused, setCarouselPaused] = useState(false);
  const animatingRef = useRef(false);
  const activeDot = quoteCount ? ((trackIndex % quoteCount) + quoteCount) % quoteCount : 0;

  useEffect(() => {
    setAnimateTrack(false);
    setTrackIndex(quoteCount);
    animatingRef.current = false;
    const id = window.requestAnimationFrame(() => setAnimateTrack(true));
    return () => window.cancelAnimationFrame(id);
  }, [lang, quoteCount]);

  const goQuote = (dir: -1 | 1) => {
    if (!quoteCount || animatingRef.current) return;
    animatingRef.current = true;
    setAnimateTrack(true);
    setTrackIndex((i) => i + dir);
  };

  const jumpToDot = (dot: number) => {
    if (!quoteCount || animatingRef.current || dot === activeDot) return;
    animatingRef.current = true;
    setAnimateTrack(true);
    // Stay in the middle copy for direct jumps
    setTrackIndex(quoteCount + dot);
  };

  const onTrackTransitionEnd = (e: React.TransitionEvent<HTMLDivElement>) => {
    if (e.propertyName !== 'transform' || !quoteCount) return;
    if (trackIndex < quoteCount) {
      setAnimateTrack(false);
      setTrackIndex(trackIndex + quoteCount);
    } else if (trackIndex >= quoteCount * 2) {
      setAnimateTrack(false);
      setTrackIndex(trackIndex - quoteCount);
    }
    animatingRef.current = false;
  };

  useEffect(() => {
    if (!animateTrack) {
      const id = window.requestAnimationFrame(() => setAnimateTrack(true));
      return () => window.cancelAnimationFrame(id);
    }
  }, [animateTrack, trackIndex]);

  useEffect(() => {
    if (carouselPaused || quoteCount <= 1) return;
    const id = window.setInterval(() => goQuote(1), 5200);
    return () => window.clearInterval(id);
    // goQuote is stable enough via refs; quoteCount/carouselPaused are deps
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [carouselPaused, quoteCount]);

  return (
    <div className="home-landing" style={{ width: '100%', fontFamily: 'Inter, system-ui, sans-serif' }}>
      <MarketingHeader v={v} lang={lang} onSetCz={onSetCz} onSetEn={onSetEn} active="about" />

      <section
        id="top"
        style={{
          position: 'relative',
          padding: 'clamp(112px,18vw,156px) 20px clamp(48px,7vw,72px)',
          background: 'radial-gradient(1100px 620px at 50% -10%, #EAF1FF 0%, rgba(234,241,255,0) 70%), #FFFFFF',
          overflow: 'hidden',
        }}
      >
        <div
          style={{
            position: 'absolute',
            inset: 0,
            backgroundImage: 'radial-gradient(#0065F4 1px, transparent 1px)',
            backgroundSize: '28px 28px',
            opacity: 0.07,
            maskImage: 'radial-gradient(700px 500px at 50% 20%, #000 20%, transparent 80%)',
            WebkitMaskImage: 'radial-gradient(700px 500px at 50% 20%, #000 20%, transparent 80%)',
          }}
        />
        <div style={{ position: 'relative', maxWidth: 1200, margin: '0 auto' }}>
          <div style={{ maxWidth: 720, margin: '0 auto', textAlign: 'center' }}>
            <h1
              style={{
                margin: 0,
                fontSize: 'clamp(34px,6.4vw,60px)',
                lineHeight: 1.06,
                letterSpacing: '-0.03em',
                fontWeight: 700,
                color: '#111827',
                textWrap: 'balance',
              }}
            >
              {c.heroTitleBefore}
              <span style={{ color: '#0065F4' }}>{c.heroTitleHighlight}</span>
              {c.heroTitleAfter}
            </h1>
            <p
              style={{
                margin: '24px auto 0',
                maxWidth: 620,
                fontSize: 'clamp(16px,2.4vw,20px)',
                lineHeight: 1.55,
                color: '#374151',
                textWrap: 'pretty',
              }}
            >
              {c.heroSub}
            </p>
          </div>

          <div
            data-hero-photo
            style={{
              marginTop: 'clamp(32px,5vw,52px)',
              width: '100%',
              aspectRatio: '16 / 8',
              borderRadius: 28,
              overflow: 'hidden',
              background: 'repeating-linear-gradient(135deg,#EAF1FF 0 10px,#F3F7FF 10px 20px)',
              border: '1px solid #E3EBFB',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              boxShadow: '0 20px 50px rgba(17,24,39,0.07)',
            }}
          >
            <span
              style={{
                fontFamily: "ui-monospace,'SF Mono',Menlo,monospace",
                fontSize: 13,
                letterSpacing: '0.08em',
                textTransform: 'uppercase',
                color: '#7FA9F7',
                textAlign: 'center',
                padding: '0 24px',
              }}
            >
              {c.heroPhotoLabel}
            </span>
          </div>

          <div
            data-stats
            style={{
              display: 'flex',
              flexWrap: 'wrap',
              justifyContent: 'center',
              gap: 'clamp(28px,6vw,72px)',
              marginTop: 'clamp(32px,5vw,48px)',
            }}
          >
            {v.stats.map((s) => (
              <div key={s.k} style={{ textAlign: 'center' }}>
                <div
                  style={{
                    fontSize: 'clamp(24px,3.4vw,32px)',
                    fontWeight: 700,
                    letterSpacing: '-0.02em',
                    color: '#111827',
                  }}
                >
                  {s.v}
                </div>
                <div style={{ fontSize: 14, color: '#6B7280', marginTop: 4 }}>{s.k}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section id="tym" style={{ background: '#F7F8FA', borderTop: '1px solid #EDF0F5', padding: 'clamp(56px,9vw,96px) 20px' }}>
        <div style={{ maxWidth: 1200, margin: '0 auto' }}>
          <div data-section-head style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', gap: 32 }}>
            <div style={{ maxWidth: 640 }}>
              <h2
                style={{
                  margin: 0,
                  fontSize: 'clamp(28px,5.2vw,44px)',
                  lineHeight: 1.12,
                  letterSpacing: '-0.025em',
                  fontWeight: 700,
                  color: '#111827',
                  textWrap: 'pretty',
                }}
              >
                {c.teamTitleBefore}
                <span style={{ color: '#0065F4' }}>{c.teamTitleHighlight}</span>
              </h2>
              <p style={{ margin: '14px 0 0', fontSize: 18, lineHeight: 1.55, color: '#374151', textWrap: 'pretty' }}>
                {c.teamSub}
              </p>
            </div>
            <button
              type="button"
              onClick={v.toggleAll}
              className="home-btn-primary"
              style={{
                flex: 'none',
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                height: 46,
                padding: '0 24px',
                background: '#0065F4',
                color: '#fff',
                fontFamily: 'inherit',
                fontSize: 15,
                fontWeight: 600,
                lineHeight: 1,
                border: 0,
                borderRadius: 10,
                cursor: 'pointer',
                transition: 'all 150ms ease-out',
              }}
            >
              {v.allLabel}
            </button>
          </div>

          <div
            data-team-grid
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(3,1fr)',
              gap: 24,
              marginTop: 'clamp(28px,4vw,44px)',
            }}
          >
            {v.team.map((p, idx) => (
              <div key={`${p.name}-${idx}`} data-flip style={{ perspective: 1400 }}>
                <div data-flip-inner style={{ position: 'relative' }}>
                  <div
                    data-flip-face
                    style={{
                      background: '#fff',
                      border: '1px solid #EDF0F5',
                      borderRadius: 20,
                      padding: '14px 14px 18px',
                      boxShadow: '0 12px 32px rgba(17,24,39,0.05)',
                    }}
                  >
                    <div
                      style={{
                        width: '100%',
                        aspectRatio: '4 / 3.4',
                        borderRadius: 14,
                        overflow: 'hidden',
                        background: 'repeating-linear-gradient(135deg,#EAF1FF 0 8px,#F3F7FF 8px 16px)',
                        border: '1px solid #E3EBFB',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      <span
                        style={{
                          fontFamily: "ui-monospace,'SF Mono',Menlo,monospace",
                          fontSize: 11,
                          letterSpacing: '0.06em',
                          textTransform: 'uppercase',
                          color: '#7FA9F7',
                        }}
                      >
                        {c.portraitLabel}
                      </span>
                    </div>
                    <div
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        gap: 16,
                        marginTop: 16,
                        padding: '0 6px',
                      }}
                    >
                      <div style={{ minWidth: 0 }}>
                        <div style={{ fontSize: 17, fontWeight: 600, letterSpacing: '-0.01em', color: '#111827' }}>
                          {p.name}
                        </div>
                        <div style={{ fontSize: 14, color: '#6B7280', marginTop: 4 }}>{p.role}</div>
                      </div>
                      <span
                        style={{
                          flex: 'none',
                          width: 36,
                          height: 36,
                          borderRadius: 999,
                          background: '#EAF1FF',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                        }}
                      >
                        <span style={{ display: 'inline-flex', transform: 'rotate(-45deg)' }}>
                          <HomeIcon name="arrow" size={16} color="#0065F4" trigger="hover" target="[data-flip]" />
                        </span>
                      </span>
                    </div>
                  </div>
                  <div
                    data-flip-face
                    data-flip-back
                    style={{
                      position: 'absolute',
                      inset: 0,
                      transform: 'rotateY(180deg)',
                      display: 'flex',
                      flexDirection: 'column',
                      background: '#0065F4',
                      border: '1px solid #0065F4',
                      borderRadius: 20,
                      padding: '28px 26px',
                      boxShadow: '0 12px 32px rgba(0,101,244,0.18)',
                    }}
                  >
                    <div style={{ fontSize: 13, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'rgba(255,255,255,0.7)' }}>
                      {p.role}
                    </div>
                    <p style={{ margin: '16px 0 0', fontSize: 16, lineHeight: 1.6, color: '#fff', textWrap: 'pretty', flex: 1 }}>
                      {p.about}
                    </p>
                    <div
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        gap: 16,
                        marginTop: 20,
                        paddingTop: 18,
                        borderTop: '1px solid rgba(255,255,255,0.22)',
                      }}
                    >
                      <div style={{ fontSize: 15, fontWeight: 600, color: '#fff' }}>{p.name}</div>
                      <a
                        href="#li"
                        aria-label="LinkedIn"
                        className="home-about-linkedin"
                        style={{
                          flex: 'none',
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: 8,
                          height: 36,
                          padding: '0 14px',
                          borderRadius: 10,
                          background: '#fff',
                          color: '#0065F4',
                          fontSize: 14,
                          fontWeight: 600,
                          lineHeight: 1,
                          transition: 'all 150ms ease-out',
                        }}
                      >
                        {c.linkedIn}
                        <span style={{ display: 'inline-flex', transform: 'rotate(-45deg)' }}>
                          <HomeIcon name="arrow" size={12} color="#0065F4" trigger="hover" target="a" />
                        </span>
                      </a>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section id="recenze" style={{ background: '#fff', borderTop: '1px solid #EDF0F5', padding: 'clamp(56px,9vw,96px) 20px' }}>
        <div style={{ maxWidth: 1200, margin: '0 auto' }}>
          <div data-section-head style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', gap: 32 }}>
            <h2
              style={{
                margin: 0,
                maxWidth: 640,
                fontSize: 'clamp(28px,5.2vw,44px)',
                lineHeight: 1.12,
                letterSpacing: '-0.025em',
                fontWeight: 700,
                color: '#111827',
                textWrap: 'pretty',
              }}
            >
              {c.reviewsTitleBefore}
              <span style={{ color: '#0065F4' }}>{c.reviewsTitleHighlight}</span>
              {c.reviewsTitleAfter}
            </h2>
            <div style={{ flex: 'none', display: 'flex', alignItems: 'center', gap: 14 }}>
              <div style={{ display: 'flex' }}>
                {v.avatars.map((a, i) => (
                  <span
                    key={`${a.i}-${i}`}
                    style={{
                      width: 38,
                      height: 38,
                      borderRadius: 999,
                      border: '2px solid #fff',
                      marginLeft: i === 0 ? 0 : -10,
                      background: a.bg,
                      display: 'inline-flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: 13,
                      fontWeight: 600,
                      color: '#fff',
                    }}
                  >
                    {a.i}
                  </span>
                ))}
              </div>
              <div>
                <div style={{ fontSize: 16, fontWeight: 700, color: '#111827', letterSpacing: '-0.01em' }}>{c.reviewsCount}</div>
                <div style={{ fontSize: 13, color: '#6B7280', marginTop: 2 }}>{c.reviewsAvg}</div>
              </div>
            </div>
          </div>

          <div
            data-quote-carousel
            onMouseEnter={() => setCarouselPaused(true)}
            onMouseLeave={() => setCarouselPaused(false)}
            onFocusCapture={() => setCarouselPaused(true)}
            onBlurCapture={(e) => {
              if (!e.currentTarget.contains(e.relatedTarget as Node | null)) {
                setCarouselPaused(false);
              }
            }}
            style={{
              position: 'relative',
              marginTop: 'clamp(28px,4vw,44px)',
            }}
          >
            <div style={{ overflow: 'hidden' }}>
              <div
                onTransitionEnd={onTrackTransitionEnd}
                style={{
                  display: 'flex',
                  gap: 24,
                  transition: animateTrack ? 'transform 480ms cubic-bezier(0.2,0.7,0.2,1)' : 'none',
                  transform: `translateX(calc(${-trackIndex} * (min(420px, 88vw) + 24px)))`,
                  willChange: 'transform',
                }}
              >
                {loopQuotes.map((q, i) => (
                  <div
                    key={`${q.name}-${i}`}
                    aria-hidden={i % quoteCount !== activeDot}
                    style={{
                      width: 'min(420px, 88vw)',
                      flex: '0 0 min(420px, 88vw)',
                      display: 'flex',
                      flexDirection: 'column',
                      background: '#fff',
                      border: '1px solid #EDF0F5',
                      borderRadius: 18,
                      padding: 28,
                      minHeight: 220,
                      boxShadow: '0 12px 32px rgba(17,24,39,0.04)',
                    }}
                  >
                    <div style={{ fontSize: 15, letterSpacing: 2, lineHeight: 1, color: '#111827' }}>{q.stars}</div>
                    <p style={{ margin: '18px 0 0', fontSize: 16, lineHeight: 1.6, color: '#111827', textWrap: 'pretty', flex: 1 }}>
                      {q.text}
                    </p>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 24 }}>
                      <span
                        style={{
                          width: 28,
                          height: 28,
                          borderRadius: 999,
                          background: '#EAF1FF',
                          display: 'inline-flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          flex: '0 0 28px',
                        }}
                      >
                        <HomeIcon name="avatar" size={15} color="#0065F4" trigger="loop-on-hover" />
                      </span>
                      <div style={{ fontSize: 14, color: '#374151' }}>{q.name}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 16,
                marginTop: 28,
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }} role="tablist" aria-label="Recenze">
                {v.quotes.map((q, i) => (
                  <button
                    key={q.name}
                    type="button"
                    role="tab"
                    aria-selected={i === activeDot}
                    aria-label={`${i + 1} / ${quoteCount}`}
                    onClick={() => jumpToDot(i)}
                    style={{
                      width: i === activeDot ? 28 : 8,
                      height: 8,
                      borderRadius: 999,
                      border: 0,
                      padding: 0,
                      cursor: 'pointer',
                      background: i === activeDot ? '#0065F4' : '#D1D5DB',
                      transition: 'width 220ms ease-out, background 150ms ease-out',
                    }}
                  />
                ))}
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <button
                  type="button"
                  aria-label={lang === 'cs' ? 'Předchozí recenze' : 'Previous review'}
                  onClick={() => goQuote(-1)}
                  className="home-about-carousel-btn"
                  style={{
                    width: 42,
                    height: 42,
                    borderRadius: 12,
                    border: '1px solid #E5E7EB',
                    background: '#fff',
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    cursor: 'pointer',
                    transition: 'all 150ms ease-out',
                  }}
                >
                  <span style={{ display: 'inline-flex', transform: 'rotate(180deg)' }}>
                    <HomeIcon name="arrow" size={16} color="#111827" trigger="hover" target="button" />
                  </span>
                </button>
                <button
                  type="button"
                  aria-label={lang === 'cs' ? 'Další recenze' : 'Next review'}
                  onClick={() => goQuote(1)}
                  className="home-about-carousel-btn"
                  style={{
                    width: 42,
                    height: 42,
                    borderRadius: 12,
                    border: '1px solid #E5E7EB',
                    background: '#fff',
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    cursor: 'pointer',
                    transition: 'all 150ms ease-out',
                  }}
                >
                  <HomeIcon name="arrow" size={16} color="#111827" trigger="hover" target="button" />
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>

      <footer style={{ background: '#fff', borderTop: '1px solid #EDF0F5', padding: 'clamp(44px,7vw,64px) 20px 32px' }}>
        <div style={{ maxWidth: 1200, margin: '0 auto' }}>
          <div data-footer-grid style={{ display: 'grid', gridTemplateColumns: '1.6fr 1fr 1fr 1fr 1fr', gap: 40 }}>
            <div>
              <img src="/home/ompli-logo-blue.png" alt="Ompli" style={{ height: 24, width: 'auto', display: 'block' }} />
              <p style={{ margin: '16px 0 0', fontSize: 15, lineHeight: 1.6, color: '#6B7280', maxWidth: 260, textWrap: 'pretty' }}>
                {c.footerTagline}
              </p>
              <div style={{ display: 'flex', gap: 10, marginTop: 20 }}>
                <a
                  href="#li"
                  aria-label="LinkedIn"
                  className="home-social"
                  style={{
                    width: 34,
                    height: 34,
                    borderRadius: 10,
                    background: '#F7F8FA',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: '#6B7280',
                    transition: 'all 150ms ease-out',
                  }}
                >
                  <svg width="17" height="17" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                    <path d="M4.98 3.5a2.5 2.5 0 1 1 0 5 2.5 2.5 0 0 1 0-5zM2.75 9.25h4.46V21H2.75V9.25zm7.02 0h4.27v1.6h.06c.6-1.07 2.06-1.94 3.79-1.94 3.24 0 4.36 1.94 4.36 5.35V21h-4.46v-5.72c0-1.44-.52-2.42-1.8-2.42-1.05 0-1.67.71-1.94 1.4-.1.24-.13.58-.13.92V21H9.77s.06-10.63 0-11.75z" />
                  </svg>
                </a>
                <a
                  href="#fb"
                  aria-label="Facebook"
                  className="home-social"
                  style={{
                    width: 34,
                    height: 34,
                    borderRadius: 10,
                    background: '#F7F8FA',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: '#6B7280',
                    transition: 'all 150ms ease-out',
                  }}
                >
                  <svg width="17" height="17" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                    <path d="M22 12.06C22 6.5 17.52 2 12 2S2 6.5 2 12.06c0 5.02 3.66 9.18 8.44 9.94v-7.03H7.9v-2.91h2.54V9.85c0-2.52 1.49-3.91 3.77-3.91 1.09 0 2.24.2 2.24.2v2.47h-1.26c-1.24 0-1.63.78-1.63 1.57v1.88h2.78l-.45 2.91h-2.33V22c4.78-.76 8.44-4.92 8.44-9.94z" />
                  </svg>
                </a>
                <a
                  href="#ig"
                  aria-label="Instagram"
                  className="home-social"
                  style={{
                    width: 34,
                    height: 34,
                    borderRadius: 10,
                    background: '#F7F8FA',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: '#6B7280',
                    transition: 'all 150ms ease-out',
                  }}
                >
                  <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true">
                    <rect x="3" y="3" width="18" height="18" rx="5" />
                    <circle cx="12" cy="12" r="4" />
                    <circle cx="17.3" cy="6.7" r="1.1" fill="currentColor" stroke="none" />
                  </svg>
                </a>
              </div>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              <div style={{ fontSize: 14, fontWeight: 600, color: '#111827' }}>{c.footerProduct}</div>
              <a href="/#funkce" className="home-footer-link" style={{ fontSize: 15, color: '#6B7280' }}>{c.footerShifts}</a>
              <a href="/#funkce" className="home-footer-link" style={{ fontSize: 15, color: '#6B7280' }}>{c.footerAttendance}</a>
              <a href="/#funkce" className="home-footer-link" style={{ fontSize: 15, color: '#6B7280' }}>{c.footerContracts}</a>
              <a href="/#funkce" className="home-footer-link" style={{ fontSize: 15, color: '#6B7280' }}>{c.footerHiring}</a>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              <div style={{ fontSize: 14, fontWeight: 600, color: '#111827' }}>{c.footerCompany}</div>
              <a href="#top" className="home-footer-link" style={{ fontSize: 15, color: '#6B7280' }}>{c.footerAbout}</a>
              <a href="#kariera" className="home-footer-link" style={{ fontSize: 15, color: '#6B7280' }}>{c.footerCareers}</a>
              <a href="/blog" className="home-footer-link" style={{ fontSize: 15, color: '#6B7280' }}>{c.footerBlog}</a>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              <div style={{ fontSize: 14, fontWeight: 600, color: '#111827' }}>{c.footerSupport}</div>
              <a href="/docs" className="home-footer-link" style={{ fontSize: 15, color: '#6B7280' }}>{c.footerHelp}</a>
              <a href="/kontakt" className="home-footer-link" style={{ fontSize: 15, color: '#6B7280' }}>{c.footerContact}</a>
              <a href="#status" className="home-footer-link" style={{ fontSize: 15, color: '#6B7280' }}>{c.footerStatus}</a>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              <div style={{ fontSize: 14, fontWeight: 600, color: '#111827' }}>{c.footerLegal}</div>
              <a href="#gdpr" className="home-footer-link" style={{ fontSize: 15, color: '#6B7280' }}>{c.footerGdpr}</a>
              <a href="#podminky" className="home-footer-link" style={{ fontSize: 15, color: '#6B7280' }}>{c.footerTerms}</a>
              <a href="#cookies" className="home-footer-link" style={{ fontSize: 15, color: '#6B7280' }}>{c.footerCookies}</a>
            </div>
          </div>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 24,
              flexWrap: 'wrap',
              marginTop: 48,
              paddingTop: 24,
              borderTop: '1px solid #EDF0F5',
            }}
          >
            <span style={{ fontSize: 14, color: '#6B7280' }}>© 2026 Ompli s.r.o.</span>
            <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
              <span style={{ fontSize: 14, color: '#6B7280' }}>{v.langLabel} · CZK</span>
              <a href="#privacy" className="home-footer-link" style={{ fontSize: 14, color: '#6B7280' }}>{c.footerPrivacy}</a>
              <a href="#terms" className="home-footer-link" style={{ fontSize: 14, color: '#6B7280' }}>{c.footerTermsShort}</a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default AboutLanding;
